import {
  useMemo, useCallback, MouseEvent, ChangeEvent,
} from "react"
import { useHistory, useLocation } from "react-router-dom"
import { GuestSummary, DioboxEvent } from "sharedTypes"
import { useShallow } from "zustand/shallow"
import useGuestsStore from "services/useGuestsStore"
import { eventPath } from "utilities/routes"
import { deselectGuest, selectGuest } from "context/actions"

type UseGuestSelectionArgs = {
  guests: GuestSummary[]
  allSelected: boolean
  event: DioboxEvent
  enableNavigation?: boolean
}

type UseGuestSelectionReturn = {
  selectedGuestIds: string[]
  selectedGuestIdsSet: Set<string>
  handleSelectionChange: (
    e: MouseEvent<HTMLElement> | ChangeEvent<HTMLInputElement>,
    guest: GuestSummary,
    checked?: boolean,
  ) => void
  handleClick: (
    e: MouseEvent<HTMLElement>,
    guest: GuestSummary
  ) => void
  deselectAll: () => void
  addSelectedGuestId: (id: string) => void
  removeSelectedGuestId: (id: string) => void
}

const useGuestSelection = ({
  guests,
  allSelected,
  event,
  enableNavigation = false,
}: UseGuestSelectionArgs): UseGuestSelectionReturn => {
  const history = useHistory()
  const location = useLocation()

  const {
    selectedGuestIds,
    addSelectedGuestId,
    removeSelectedGuestId,
    deselectAll,
  } = useGuestsStore(
    useShallow((state) => ({
      selectedGuestIds: state.selectedGuestIds,
      addSelectedGuestId: state.addSelectedGuestId,
      removeSelectedGuestId: state.removeSelectedGuestId,
      deselectAll: state.deselectAll,
    })),
  )

  // Convert to Set for O(1) lookup performance
  const selectedGuestIdsSet = useMemo(
    () => new Set(selectedGuestIds),
    [selectedGuestIds],
  )

  const guestsToSelect = useCallback((guest: GuestSummary) => {
    const currentIds = useGuestsStore.getState().selectedGuestIds
    const guestIndex = guests.findIndex((g) => g.id === guest.id)
    const firstSelectedGuestIndex = guests.findIndex(
      (g) => g.id === currentIds[0],
    )

    if (guestIndex < firstSelectedGuestIndex) {
      return guests.slice(guestIndex, firstSelectedGuestIndex)
    }

    const lastSelectedGuestIndex = guests.findIndex(
      (g) => g.id === currentIds[currentIds.length - 1],
    )

    return guests.slice(lastSelectedGuestIndex + 1, guestIndex + 1)
  }, [guests])

  const changeSelection = useCallback((
    selected: boolean,
    selectMultiple: boolean,
    guest: GuestSummary,
  ) => {
    if (allSelected) {
      deselectAll()

      return
    }

    if (selected) {
      const currentIds = useGuestsStore.getState().selectedGuestIds
      if (selectMultiple && currentIds.length > 0) {
        guestsToSelect(guest).forEach((g) => selectGuest(addSelectedGuestId, g))
      } else {
        selectGuest(addSelectedGuestId, guest)
      }
    } else {
      deselectGuest(removeSelectedGuestId, guest)
    }
  }, [
    allSelected,
    guestsToSelect,
    addSelectedGuestId,
    removeSelectedGuestId,
    deselectAll,
  ])

  const handleSelectionChange = useCallback((
    e: MouseEvent<HTMLElement> | ChangeEvent<HTMLInputElement>,
    guest: GuestSummary,
    checked?: boolean,
  ) => {
    const isChecked = checked ?? (e.target as HTMLInputElement).checked
    const hasShiftKey = "nativeEvent" in e && "shiftKey" in e.nativeEvent
      ? e.nativeEvent.shiftKey
      : false
    changeSelection(isChecked, hasShiftKey, guest)
  }, [changeSelection])

  const handleClick = useCallback((
    e: MouseEvent<HTMLElement>,
    guest: GuestSummary,
  ) => {
    const { selectedGuestIds: currentIds } = useGuestsStore.getState()
    const multipleSelectEnabled = currentIds.length > 0

    // If multiple selection is enabled, handle selection logic
    if (multipleSelectEnabled) {
      e.preventDefault()
      const isSelected = currentIds.includes(guest.id)
      changeSelection(
        !isSelected,
        e.nativeEvent.shiftKey,
        guest,
      )

      return
    }

    // If navigation enabled, navigate to the guest (single selection)
    if (enableNavigation) {
      e.preventDefault()
      history.push({
        pathname: `${eventPath(event.id!)}/guests/${guest.id}`,
        state: { background: location },
      })
    }
  }, [
    changeSelection,
    enableNavigation,
    history,
    location,
    event.id,
  ])

  return {
    selectedGuestIds,
    selectedGuestIdsSet,
    handleSelectionChange,
    handleClick,
    deselectAll,
    addSelectedGuestId,
    removeSelectedGuestId,
  }
}

export default useGuestSelection
